import * as THREE from 'three';

/**
 * Minimal structural types for @cinevva/usdjs layer data (avoids relying on three's USDA string parser + JSON).
 */
type SdfValue = unknown;

type SdfPropertySpec = {
  typeName?: string;
  defaultValue?: SdfValue;
  timeSamples?: Map<number, SdfValue>;
};

type SdfPrim = {
  path: { primPath: string };
  specifier?: string;
  typeName?: string;
  children?: Map<string, SdfPrim>;
  properties?: Map<string, SdfPropertySpec>;
};

type SdfLayerLike = {
  root: SdfPrim;
};

/** Anything that exposes usdjs composed prim stack (UsdStage.composePrimIndex). */
export type UsdStageLike = {
  composePrimIndex(): unknown;
};

function resolvePropertyValue(spec: SdfPropertySpec): SdfValue | undefined {
  if (spec.defaultValue !== undefined) return spec.defaultValue;
  if (spec.timeSamples && spec.timeSamples.size > 0) {
    const keys = [...spec.timeSamples.keys()].sort((a, b) => a - b);
    return spec.timeSamples.get(keys[0]!);
  }
  return undefined;
}

/** Flatten numeric arrays from usdjs SdfValue (typedArray, tuple arrays, vec types). */
function valueToFloatArray(v: SdfValue): Float32Array | null {
  if (v === null || v === undefined) return null;
  if (typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  if (o.type === 'typedArray' && o.value instanceof Float32Array) return o.value as Float32Array;
  if (o.type === 'typedArray' && o.value instanceof Float64Array)
    return Float32Array.from(o.value as Float64Array);
  if (o.type === 'typedArray' && (o.value instanceof Int32Array || o.value instanceof Uint32Array))
    return Float32Array.from(o.value as ArrayLike<number>);
  if (o.type === 'vec2f' || o.type === 'vec3f' || o.type === 'vec4f')
    return Float32Array.from((o.value as number[]) ?? []);
  if (o.type === 'array' && Array.isArray(o.value)) {
    const out: number[] = [];
    for (const el of o.value as SdfValue[]) {
      const inner = valueToFloatArray(el);
      if (inner) {
        for (let i = 0; i < inner.length; i++) out.push(inner[i]!);
      }
    }
    return out.length ? Float32Array.from(out) : null;
  }
  if (o.type === 'tuple' && Array.isArray(o.value)) {
    const out: number[] = [];
    for (const el of o.value as SdfValue[]) {
      const inner = valueToFloatArray(el);
      if (inner) {
        for (let i = 0; i < inner.length; i++) out.push(inner[i]!);
      } else if (typeof el === 'number') out.push(el);
    }
    return out.length ? Float32Array.from(out) : null;
  }
  return null;
}

function valueToIntArray(v: SdfValue): Int32Array | null {
  if (v === null || v === undefined) return null;
  if (typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  if (o.type === 'typedArray' && o.value instanceof Int32Array) return o.value as Int32Array;
  if (o.type === 'typedArray' && o.value instanceof Uint32Array)
    return Int32Array.from(o.value as ArrayLike<number>);
  if (o.type === 'typedArray' && o.value instanceof Uint16Array)
    return Int32Array.from(o.value as ArrayLike<number>);
  if (o.type === 'array' && Array.isArray(o.value)) {
    const nums: number[] = [];
    for (const el of o.value as SdfValue[]) {
      if (typeof el === 'number') nums.push(el);
      else if (el && typeof el === 'object' && (el as { type?: string }).type === 'typedArray') {
        const a = valueToIntArray(el);
        if (a) nums.push(...Array.from(a));
      }
    }
    return nums.length ? Int32Array.from(nums) : null;
  }
  return null;
}

function collectMeshProps(prim: SdfPrim): {
  points: Float32Array | null;
  faceVertexIndices: Int32Array | null;
  faceVertexCounts: Int32Array | null;
} {
  let points: Float32Array | null = null;
  let faceVertexIndices: Int32Array | null = null;
  let faceVertexCounts: Int32Array | null = null;
  if (!prim.properties) return { points, faceVertexIndices, faceVertexCounts };

  for (const [key, spec] of prim.properties) {
    const val = resolvePropertyValue(spec);
    const tn = spec.typeName ?? '';
    const isPoints =
      key === 'points' ||
      key.endsWith(':points') ||
      (tn.includes('point3f') && key.includes('points'));
    const isFvi =
      key.includes('faceVertexIndices') || (tn.includes('int') && key.includes('faceVertexIndices'));
    const isFvc =
      key.includes('faceVertexCounts') || (tn.includes('int') && key.includes('faceVertexCounts'));

    if (isPoints && !points) points = valueToFloatArray(val);
    if (isFvi && !faceVertexIndices) faceVertexIndices = valueToIntArray(val);
    if (isFvc && !faceVertexCounts) faceVertexCounts = valueToIntArray(val);
  }
  return { points, faceVertexIndices, faceVertexCounts };
}

/** Partitioned USD faces → triangle index list (same rules as Three.js USDAParser for 3/4-gons + fan). */
function triangulateFaceIndices(faceVertexIndices: number[], faceVertexCounts: number[]): number[] {
  const out: number[] = [];
  let offset = 0;
  for (let fi = 0; fi < faceVertexCounts.length; fi++) {
    const count = faceVertexCounts[fi]!;
    const face = faceVertexIndices.slice(offset, offset + count);
    offset += count;
    if (count === 3) {
      out.push(face[0]!, face[1]!, face[2]!);
    } else if (count === 4) {
      const [a, b, c, d] = face;
      out.push(a!, b!, c!, a!, c!, d!);
    } else if (count > 3) {
      for (let k = 1; k < count - 1; k++) {
        out.push(face[0]!, face[k]!, face[k + 1]!);
      }
    }
  }
  return out;
}

function meshFromPrim(prim: SdfPrim): THREE.Mesh | null {
  const { points, faceVertexIndices, faceVertexCounts } = collectMeshProps(prim);
  if (!points || points.length < 9) return null;

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(points, 3));

  if (faceVertexIndices && faceVertexCounts && faceVertexCounts.length > 0) {
    const fvi = Array.from(faceVertexIndices);
    const fvc = Array.from(faceVertexCounts);
    const tri = triangulateFaceIndices(fvi, fvc);
    if (tri.length > 0) geom.setIndex(tri);
  } else if (faceVertexIndices && faceVertexIndices.length % 3 === 0) {
    geom.setIndex(Array.from(faceVertexIndices));
  }

  geom.computeVertexNormals();

  const mat = new THREE.MeshStandardMaterial({
    color: 0xaeaeae,
    metalness: 0.2,
    roughness: 0.75,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geom, mat);
  const name = prim.path.primPath.split('/').filter(Boolean).pop();
  if (name) mesh.name = name;
  return mesh;
}

function applyXformMatrix(prim: SdfPrim, obj: THREE.Object3D) {
  if (!prim.properties) return;
  for (const [, spec] of prim.properties) {
    const tn = spec.typeName ?? '';
    const val = resolvePropertyValue(spec);
    if (!val || typeof val !== 'object') continue;
    const o = val as Record<string, unknown>;
    if (tn.includes('matrix4') && o.type === 'matrix4d' && Array.isArray(o.value) && o.value.length >= 16) {
      const arr = o.value as number[];
      obj.matrix.fromArray(arr);
      obj.matrix.decompose(obj.position, obj.quaternion, obj.scale);
      return;
    }
  }
}

function walkPrim(prim: SdfPrim, parent: THREE.Object3D) {
  const tn = prim.typeName ?? '';

  if (tn === 'Mesh') {
    const mesh = meshFromPrim(prim);
    if (mesh) {
      applyXformMatrix(prim, mesh);
      parent.add(mesh);
    }
    return;
  }

  const node = new THREE.Group();
  const label = prim.path.primPath.split('/').filter(Boolean).pop();
  if (label && prim.path.primPath !== '/') node.name = label;
  applyXformMatrix(prim, node);
  parent.add(node);

  if (prim.children) {
    for (const child of prim.children.values()) {
      walkPrim(child, node);
    }
  }
}

/**
 * Builds a Three.js group from a composed usdjs stage without round-tripping through Three's USDA parser.
 */
export function buildThreeGroupFromUsdStage(stage: UsdStageLike): THREE.Group {
  const composed = stage.composePrimIndex() as SdfLayerLike;
  const root = new THREE.Group();
  root.name = 'usd-root';
  if (composed.root.children) {
    for (const child of composed.root.children.values()) {
      walkPrim(child, root);
    }
  }
  return root;
}
